import type { JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AsyncState } from '@/components/async';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { useDashboardPropertyDetail } from '@/hooks/useDashboardProperties';
import {
  UnitCreateForm,
  type UnitCreateNextStep,
} from '@/routes/dashboard/unitCreate/UnitCreateForm';
import { formatPropertyAddress } from '@/lib/utils/propertyFormatting';

export function DashboardUnitCreate(): JSX.Element {
  const user = useDashboardUser();
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const normalizedPropertyId = (propertyId ?? '').trim();

  const detailQuery = useDashboardPropertyDetail({
    user,
    propertyId: normalizedPropertyId.length > 0 ? normalizedPropertyId : null,
  });

  const backToProperty = () => {
    navigate(`/dashboard/properties/${normalizedPropertyId}`);
  };

  const skeleton = (
    <div className="bg-surface-muted h-28 animate-pulse rounded-3xl border border-surface-subtle" />
  );

  return (
    <section className="flex flex-col gap-xl py-8">
      <button
        type="button"
        onClick={backToProperty}
        className="min-h-11 w-fit rounded-full border border-brand-primary/40 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-brand-primary transition hover:bg-brand-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
      >
        Back to property
      </button>

      <AsyncState
        data={detailQuery.data ?? null}
        isLoading={detailQuery.isLoading || detailQuery.isFetching}
        error={detailQuery.error}
        skeleton={skeleton}
      >
        {(detail) => {
          const propertyLabel = formatPropertyAddress(detail.property.address);

          const handleCreated = (created: { id?: string }, nextStep: UnitCreateNextStep) => {
            switch (nextStep) {
              case 'createAnother':
                return;
              case 'viewUnit': {
                const unitId = typeof created.id === 'string' ? created.id.trim() : '';
                if (unitId.length === 0) {
                  navigate(`/dashboard/properties/${normalizedPropertyId}`);
                  return;
                }
                navigate(`/dashboard/properties/${normalizedPropertyId}/units/${unitId}`);
                return;
              }
              case 'backToProperty':
              default:
                navigate(`/dashboard/properties/${normalizedPropertyId}`);
                return;
            }
          };

          return (
            <>
              <header className="flex flex-col gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-brand-primary/70">
                  Property inventory
                </p>
                <h1 className="text-3xl font-semibold text-text-primary">Create unit</h1>
                <p className="text-sm text-text-primary/70">{propertyLabel}</p>
              </header>

              <div className="rounded-3xl border border-surface-subtle bg-white p-6 shadow-sm">
                <UnitCreateForm
                  propertyId={normalizedPropertyId}
                  onCancel={backToProperty}
                  onCreated={handleCreated}
                />
              </div>
            </>
          );
        }}
      </AsyncState>
    </section>
  );
}
